import { useState } from 'react';
import { useApp } from '@/contexts/AppContext';
import { useAuth } from '@/contexts/AuthContext';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import {
  Pencil, Star, Archive, Trash, Plus, Lock,
  FileText, Inbox, Zap, BookOpen, GitBranch
} from 'lucide-react';
import SidebarNavItem from '@/components/SidebarNavItem';
import SidebarNotebookList from '@/components/SidebarNotebookList';
import SidebarTagList from '@/components/SidebarTagList';
import SidebarFooter from '@/components/SidebarFooter';
import NotebookDialog from '@/components/NotebookDialog';
import TagManager from '@/components/TagManager';
import TemplateDialog from '@/components/TemplateDialog';
import ImportExport from '@/components/ImportExport';
import SettingsDialog from '@/components/SettingsDialog';
import QuickCapture from '@/components/QuickCapture';
import { toast } from 'sonner';

export default function Sidebar({ onNavigate }) {
  const {
    activeView, setActiveView,
    filterNotebookId, setFilterNotebookId,
    filterTagId, setFilterTagId,
    notebooks, tags, notes,
    noteCountByNotebook, noteCountByTag,
    createNote, setSelectedNoteId, updateNote,
  } = useApp();
  const { user, logout, isEncryptionEnabled, isVaultLocked } = useAuth();
  const [showNotebookDialog, setShowNotebookDialog] = useState(false);
  const [showTagManager, setShowTagManager] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [showImportExport, setShowImportExport] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showQuickCapture, setShowQuickCapture] = useState(false);
  const [dragOverNotebook, setDragOverNotebook] = useState(null);

  const allCount = notes.filter((n) => !n.is_archived && !n.is_deleted && !n.is_journal).length;
  const favCount = notes.filter((n) => n.is_favorited && !n.is_archived && !n.is_deleted).length;
  const archiveCount = notes.filter((n) => n.is_archived && !n.is_deleted).length;
  const trashCount = notes.filter((n) => n.is_deleted).length;
  const inboxNotebook = notebooks.find((nb) => nb.name === 'Inbox');
  const inboxCount = inboxNotebook ? (noteCountByNotebook[inboxNotebook.id] || 0) : 0;
  const journalCount = notes.filter(n => n.is_journal && !n.is_archived && !n.is_deleted).length;

  const handleNavClick = (view, notebookId = null, tagId = null) => {
    setActiveView(view);
    setFilterNotebookId(notebookId);
    setFilterTagId(tagId);
    setSelectedNoteId(null);
    onNavigate?.();
  };

  const handleNewNote = async () => {
    setActiveView('all');
    setFilterNotebookId(null);
    setFilterTagId(null);
    await createNote();
    onNavigate?.();
  };

  const handleNoteDrop = async (notebookId, e) => {
    e.preventDefault();
    setDragOverNotebook(null);
    try {
      const data = e.dataTransfer.getData('application/vaultnotes-note');
      if (!data) return;
      const { noteId, title } = JSON.parse(data);
      const notebook = notebooks.find(nb => nb.id === notebookId);
      await updateNote(noteId, { notebook_id: notebookId });
      toast.success(`Moved "${title || 'Note'}" to ${notebook?.name || 'notebook'}`);
    } catch (err) {
      console.error('Drop error:', err);
      toast.error('Failed to move note');
    }
  };

  const handleNoteDropToAll = async (e) => {
    e.preventDefault();
    setDragOverNotebook(null);
    try {
      const data = e.dataTransfer.getData('application/vaultnotes-note');
      if (!data) return;
      const { noteId, title } = JSON.parse(data);
      await updateNote(noteId, { notebook_id: null });
      toast.success(`Removed "${title || 'Note'}" from notebook`);
    } catch (err) {
      console.error('Drop error:', err);
      toast.error('Failed to move note');
    }
  };

  const dragProps = { dragOverNotebook, setDragOverNotebook, handleNoteDrop, handleNoteDropToAll };

  return (
    <div className="flex flex-col h-full w-full" data-testid="sidebar">
      {/* Header */}
      <div className="p-4 border-b border-border/50">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-accent-amber" />
            <span className="font-semibold text-base tracking-tight">VaultNotes</span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleNewNote}
            className="flex-1 bg-accent-amber text-black hover:bg-accent-amber/90 font-medium text-sm h-10"
            data-testid="sidebar-new-note-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            New Note
          </Button>
          <Button
            onClick={() => setShowQuickCapture(true)}
            variant="outline"
            className="h-10 px-3"
            title="Quick Capture (Ctrl+Shift+N)"
            data-testid="sidebar-quick-capture-btn"
          >
            <Zap className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-0.5">
          <SidebarNavItem
            icon={Pencil} label="All Notes" count={allCount}
            active={activeView === 'all' && !filterNotebookId && !filterTagId}
            onClick={() => handleNavClick('all')}
            testId="sidebar-all-notes" droppable onDropToAll
            {...dragProps}
          />
          <SidebarNavItem
            icon={Star} label="Favorites" count={favCount}
            active={activeView === 'favorites'}
            onClick={() => handleNavClick('favorites')}
            testId="sidebar-favorites"
          />
          {inboxNotebook && (
            <SidebarNavItem
              icon={Inbox} label="Inbox" count={inboxCount}
              active={filterNotebookId === inboxNotebook.id}
              onClick={() => handleNavClick('all', inboxNotebook.id, null)}
              testId="sidebar-inbox" droppable notebookId={inboxNotebook.id}
              {...dragProps}
            />
          )}
          <SidebarNavItem
            icon={BookOpen} label="Journal" count={journalCount}
            active={activeView === 'journal'}
            onClick={() => handleNavClick('journal')}
            testId="sidebar-journal"
          />

          <SidebarNotebookList
            notebooks={notebooks} noteCountByNotebook={noteCountByNotebook}
            filterNotebookId={filterNotebookId} handleNavClick={handleNavClick}
            onAddNotebook={() => setShowNotebookDialog(true)}
            {...dragProps}
          />

          <SidebarTagList
            tags={tags} noteCountByTag={noteCountByTag}
            filterTagId={filterTagId} handleNavClick={handleNavClick}
            onAddTag={() => setShowTagManager(true)}
          />

          <div className="pt-4 space-y-0.5">
            <SidebarNavItem
              icon={GitBranch} label="Graph View"
              active={activeView === 'graph'}
              onClick={() => handleNavClick('graph')}
              testId="sidebar-graph-view"
            />
            <SidebarNavItem
              icon={FileText} label="Templates" active={false}
              onClick={() => setShowTemplateDialog(true)}
              testId="sidebar-templates"
            />
            <SidebarNavItem
              icon={Archive} label="Archive" count={archiveCount}
              active={activeView === 'archived'}
              onClick={() => handleNavClick('archived')}
              testId="sidebar-archive"
            />
            <SidebarNavItem
              icon={Trash} label="Trash" count={trashCount}
              active={activeView === 'trash'}
              onClick={() => handleNavClick('trash')}
              testId="sidebar-trash"
            />
          </div>
        </div>
      </ScrollArea>

      <SidebarFooter
        user={user} logout={logout}
        isEncryptionEnabled={isEncryptionEnabled} isVaultLocked={isVaultLocked}
        onOpenImportExport={() => setShowImportExport(true)}
        onOpenSettings={() => setShowSettings(true)}
      />

      {/* Dialogs */}
      {showNotebookDialog && <NotebookDialog onClose={() => setShowNotebookDialog(false)} />}
      {showTagManager && <TagManager onClose={() => setShowTagManager(false)} />}
      {showTemplateDialog && <TemplateDialog onClose={() => setShowTemplateDialog(false)} />}
      {showImportExport && <ImportExport onClose={() => setShowImportExport(false)} />}
      {showSettings && <SettingsDialog onClose={() => setShowSettings(false)} />}
      {showQuickCapture && <QuickCapture open={showQuickCapture} onClose={() => setShowQuickCapture(false)} />}
    </div>
  );
}
